import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import {
  FormUpload,
  FormSelect,
  PrimaryButton,
  RedButton,
  SecondaryButton,
} from "../../components";
import { withProtectedRoute } from "../../utils/HOC/withProtectedRoute";
import { useAppSettings } from "../../store/useAppSettings";
import { useUploadImage } from "../../store/useUploadImage";
import { useAlert } from "../../store/useAlert";
import { usePartners } from "../../store/usePartners";

function DropbotLoadingAdScreen() {
  /** Hooks */
  const navigate = useNavigate();
  const { setAlert } = useAlert();
  const {
    appSettings,
    getAppSettings,
    updateAppSettings,
    updatedSettings,
    clearUpdatedSettings,
  } = useAppSettings();
  const { uploadImage, clearUploadedImage, uploadedImage, setUploadedImage } =
    useUploadImage();
  const { allPartners, getPartnersForSelect } = usePartners();

  /** State */
  const [image, setImage] = useState<File | null>(null);
  const [adType, setAdType] = useState<"image" | "partner">("image");
  const [selectedPartnerId, setSelectedPartnerId] = useState<string>("");

  useEffect(() => {
    getAppSettings();
    getPartnersForSelect();
    return () => {
      clearUploadedImage();
      clearUpdatedSettings();
    };
  }, [
    getAppSettings,
    getPartnersForSelect,
    clearUploadedImage,
    clearUpdatedSettings,
  ]);

  useEffect(() => {
    if (appSettings) {
      // Check if there's a partner ID set
      if (appSettings.dropbotPartnerId) {
        setAdType("partner");
        setSelectedPartnerId(appSettings.dropbotPartnerId);
        setUploadedImage(appSettings.dropbotLoadingAd || null);
      } else if (appSettings.dropbotLoadingAd) {
        // Only image ad
        setAdType("image");
        setUploadedImage(appSettings.dropbotLoadingAd);
        setSelectedPartnerId("");
      } else {
        // No ad set
        setAdType("image");
        setUploadedImage(null);
        setSelectedPartnerId("");
      }
    }
  }, [appSettings, setUploadedImage]);

  useEffect(() => {
    if (updatedSettings) {
      setAlert({
        isOpen: true,
        message: "Dropbot Loading Ad updated successfully!",
        severity: "success",
      });
      clearUpdatedSettings();
      setImage(null);
      // Navigate back or refresh data after successful update
      getAppSettings();
    }
  }, [updatedSettings, setAlert, clearUpdatedSettings, getAppSettings]);

  const onImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e?.target?.files) {
      setImage(e.target.files[0]);
      // Clear the uploaded image state when a new file is selected
      // This ensures the FormUpload shows the new file preview instead of the old uploaded image
      setUploadedImage(null);
    }
  };

  const onPartnerChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedPartnerId(e.target.value);
  };

  const onAdTypeChange = (type: "image" | "partner") => {
    setAdType(type);
    // Don't clear data when switching - let users toggle between modes without losing their work
  };

  const onUploadImage = () => {
    if (!image) {
      console.log("No image selected for upload");
      return;
    }
    console.log("Uploading image:", image);
    uploadImage(image);
  };

  const onSubmit = () => {
    console.log("onSubmit called", {
      adType,
      image: !!image,
      uploadedImage,
      selectedPartnerId,
    });

    if (adType === "image") {
      // If there's a new image that hasn't been uploaded yet, upload it first
      if (image && !uploadedImage) {
        console.log("Uploading image first before saving");
        uploadImage(image);
        return;
      }

      // Save image URL and remove partner ID
      const imageUrl = uploadedImage || null;
      console.log("Saving app settings with imageUrl:", imageUrl);
      updateAppSettings({
        dropbotLoadingAd: imageUrl,
        dropbotPartnerId: null,
      });
    } else if (adType === "partner") {
      if (!selectedPartnerId) {
        setAlert({
          isOpen: true,
          message: "Please select a partner",
          severity: "error",
        });
        return;
      }

      const selectedPartner = allPartners.find(
        (p) => p._id === selectedPartnerId
      );
      if (!selectedPartner) {
        setAlert({
          isOpen: true,
          message: "Selected partner not found",
          severity: "error",
        });
        return;
      }

      // Save partner logo URL to dropbotLoadingAd and partner ID to dropbotPartnerId
      const partnerLogoUrl =
        selectedPartner.image || selectedPartner.thumbnail || "";
      console.log("Saving app settings with partner:", {
        partnerId: selectedPartnerId,
        logoUrl: partnerLogoUrl,
      });
      updateAppSettings({
        dropbotLoadingAd: partnerLogoUrl,
        dropbotPartnerId: selectedPartnerId,
      });
    }
  };

  // Auto-save after image upload
  useEffect(() => {
    if (uploadedImage && image) {
      console.log("Auto-saving after image upload:", uploadedImage);
      // Image was just uploaded, now save it and clear partner ID
      updateAppSettings({
        dropbotLoadingAd: uploadedImage,
        dropbotPartnerId: null,
      });
    }
  }, [uploadedImage, image, updateAppSettings]);

  const onRemoveImage = () => {
    setImage(null);
    setUploadedImage(null);
    setSelectedPartnerId("");
    clearUploadedImage();
    // Remove both the ad and partner ID from settings
    updateAppSettings({
      dropbotLoadingAd: null,
      dropbotPartnerId: null,
    });
  };

  // Helper functions and variables for UI
  const activePartners = allPartners.filter((partner) => partner.active);
  const partnerOptions = [
    { value: "", label: "Choose a partner..." },
    ...activePartners.map((partner) => ({
      value: partner._id || "",
      label: partner.name || "Unnamed Partner",
    })),
  ];

  const selectedPartner = allPartners.find((p) => p._id === selectedPartnerId);
  const hasActiveAd = uploadedImage || selectedPartnerId;

  const goBack = () => navigate("/beevision-drops");

  return (
    <div>
      <div className="flex flex-row items-center mb-6">
        <h1 className="font-satoshi text-2xl font-bold text-dark">
          Dropbot Loading Ad
        </h1>
      </div>
      <div className="flex flex-col sm:flex-row">
        <div className="w-full sm:w-1/2 bg-white drop-shadow-none rounded-xl mb-1 sm:mb-0">
          <div className="w-full border-b border-gray-border px-[28px] py-[15px]">
            <p className="font-satoshi font-medium text-base text-dark">
              Loading Ad Settings
            </p>
          </div>
          <div className="w-full px-7 py-7">
            <div className="mb-6">
              <p className="font-satoshi font-medium text-sm text-gray-text mb-4">
                Choose how you want to display a loading advertisement in the
                Dropbot app. You can upload a custom image or select a partner
                logo.
              </p>
            </div>

            {/* Ad Type Selection */}
            <div className="mb-6">
              <p className="font-satoshi font-medium text-sm text-dark mb-3">
                Advertisement Type
              </p>
              <div className="space-y-3">
                <div
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    adType === "image"
                      ? "border-primary bg-primary/5"
                      : "border-gray-200 bg-white hover:border-gray-300"
                  }`}
                  onClick={() => onAdTypeChange("image")}
                >
                  <div className="flex items-center">
                    <div className="relative">
                      <input
                        type="radio"
                        name="adType"
                        value="image"
                        checked={adType === "image"}
                        onChange={() => onAdTypeChange("image")}
                        className="w-4 h-4 text-primary border-gray-300 focus:ring-primary focus:ring-2"
                      />
                    </div>
                    <div className="ml-3">
                      <p className="font-satoshi font-medium text-sm text-dark">
                        Upload Custom Image
                      </p>
                      <p className="font-satoshi text-xs text-gray-500">
                        Upload your own advertisement image
                      </p>
                    </div>
                  </div>
                </div>

                <div
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    adType === "partner"
                      ? "border-primary bg-primary/5"
                      : "border-gray-200 bg-white hover:border-gray-300"
                  }`}
                  onClick={() => onAdTypeChange("partner")}
                >
                  <div className="flex items-center">
                    <div className="relative">
                      <input
                        type="radio"
                        name="adType"
                        value="partner"
                        checked={adType === "partner"}
                        onChange={() => onAdTypeChange("partner")}
                        className="w-4 h-4 text-primary border-gray-300 focus:ring-primary focus:ring-2"
                      />
                    </div>
                    <div className="ml-3">
                      <p className="font-satoshi font-medium text-sm text-dark">
                        Use Partner Logo
                      </p>
                      <p className="font-satoshi text-xs text-gray-500">
                        Select from available partner logos
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {hasActiveAd && (
              <div className="mb-6">
                <p className="font-satoshi font-medium text-sm text-dark mb-3">
                  Current Status
                </p>
                <div className="p-3 bg-green-50 border border-green-200 rounded">
                  <p className="font-satoshi text-sm text-green-700">
                    ✅ Loading ad is currently active
                    {adType === "partner" && selectedPartner && (
                      <span className="block mt-1">
                        Partner: {selectedPartner.name}
                      </span>
                    )}
                  </p>
                </div>
              </div>
            )}

            {!hasActiveAd && (
              <div className="mb-6">
                <p className="font-satoshi font-medium text-sm text-dark mb-3">
                  Current Status
                </p>
                <div className="p-3 bg-gray-50 border border-gray-200 rounded">
                  <p className="font-satoshi text-sm text-gray-600">
                    ℹ️ No loading ad is currently set
                  </p>
                </div>
              </div>
            )}

            {/* Partner Selection */}
            {adType === "partner" && (
              <div className="mb-6">
                <FormSelect
                  label="Select Partner"
                  value={selectedPartnerId}
                  onChange={onPartnerChange}
                  options={partnerOptions}
                />
              </div>
            )}

            {hasActiveAd && (
              <div className="mb-6">
                <RedButton
                  onClick={onRemoveImage}
                  label="Remove Current Ad"
                  className="h-10 "
                />
              </div>
            )}

            <div className="flex flex-row items-center justify-end">
              <SecondaryButton
                type="button"
                onClick={goBack}
                label="Cancel"
                className="h-8 px-4 py-0 bg-white border border-droppie-oranje text-droppie-oranje hover:bg-droppie-oranje/5 flex items-center justify-center"
                labelClassName="text-droppie-oranje"
              />
              <PrimaryButton
                onClick={onSubmit}
                label="Save Changes"
                className="h-10 ml-4 !bg-droppie-oranje !border-droppie-oranje active:!bg-droppie-oranje-dark"
                size="sm"
              />
            </div>
          </div>
        </div>
        <div className="w-full sm:w-1/3 sm:ml-8 bg-white drop-shadow-none rounded-xl h-[360px]">
          <div className="w-full border-b border-gray-border px-[28px] py-[15px]">
            <p className="font-satoshi font-medium text-base text-dark">
              {adType === "image" ? "Loading Ad Image" : "Partner Logo Preview"}
            </p>
          </div>
          <div className="w-full px-7 py-7 h-[calc(100%-60px)]">
            {adType === "image" ? (
              <>
                <FormUpload
                  onChange={onImageChange}
                  value={image}
                  imageUrl={uploadedImage as string}
                />
                <div className="flex flex-row items-center justify-end mt-4">
                  <SecondaryButton
                    type="button"
                    onClick={goBack}
                    label="Cancel"
                    className="h-8 px-4 py-0 bg-white border border-droppie-oranje text-droppie-oranje hover:bg-droppie-oranje/5 flex items-center justify-center"
                    labelClassName="text-droppie-oranje"
                  />
                  <PrimaryButton
                    onClick={onUploadImage}
                    className="h-10 ml-4 !bg-droppie-oranje !border-droppie-oranje active:!bg-droppie-oranje-dark"
                    size="sm"
                    label="Upload"
                  />
                </div>
              </>
            ) : (
              <div className="flex flex-col h-full">
                {selectedPartner ? (
                  <div className="flex-1 flex flex-col items-center justify-center">
                    {selectedPartner.image || selectedPartner.thumbnail ? (
                      <>
                        <img
                          src={
                            selectedPartner.image || selectedPartner.thumbnail
                          }
                          alt={selectedPartner.name}
                          className="max-w-full max-h-48 object-contain rounded border"
                        />
                        <p className="font-satoshi text-sm text-gray-600 mt-3 text-center">
                          {selectedPartner.name}
                        </p>
                      </>
                    ) : (
                      <div className="flex flex-col items-center justify-center p-8 border-2 border-dashed border-gray-300 rounded">
                        <p className="font-satoshi text-sm text-gray-500 text-center">
                          No logo available for {selectedPartner.name}
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex-1 flex items-center justify-center">
                    <div className="text-center">
                      <p className="font-satoshi text-sm text-gray-500">
                        Select a partner to preview their logo
                      </p>
                    </div>
                  </div>
                )}
                <div className="flex flex-row items-center justify-end mt-4">
                  <SecondaryButton onClick={goBack} label="Cancel" />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default withProtectedRoute(DropbotLoadingAdScreen);
